import {FormsModule} from '@angular/forms';
import {APP_INITIALIZER, LOCALE_ID, NgModule, isDevMode, ErrorHandler} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {RouteReuseStrategy} from '@angular/router';
import {IonicModule, IonicRouteStrategy} from '@ionic/angular';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {OrganisationsService} from './services/core/organisations.service';
import {HttpService} from './services/core/http.service';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {registerLocaleData} from '@angular/common';
import de from '@angular/common/locales/de';
import {StorageService} from './services/core/storage.service';
import {AuthService} from './services/core/auth.service';
import {UserService} from './services/core/user.service';
import {ServiceFacilitiesService} from './services/core/service-facilities.service';
import {DeviceService} from './services/core/device.service';
import {AuthInterceptor} from './interceptors/auth.interceptor';
import {localeDe, setOptions} from '@mobiscroll/angular-ivy';
import {CustomersService} from './services/customers/customers.service';
import {SyncService} from './services/core/sync.service';
import {DeviceInterceptor} from './interceptors/device.interceptor';
import {ServiceWorkerModule} from '@angular/service-worker';
import {SyncStatusItemsTable} from './tables/sync-status-items.table';
import {ConnectionService} from './services/core/connection.service';
import {StdToolbarComponent} from './library/std-toolbar/std-toolbar.component';
import {VehiclesTable} from './tables/time-tracking/vehicles.table';
import {EmployeesTable} from './tables/core/employees.table';
import {Settings} from 'luxon';
import {ProdRequestsTable} from './tables/product-management/prod-requests.table';
import {LicensesService} from './services/core/licenses.service';
import * as Sentry from "@sentry/angular";
import {environment} from '../environments/environment';
import {Chart, registerables} from 'chart.js';
import {SettingsService} from './services/core/settings.service';
import {TranslateModule} from '@ngx-translate/core';
import {TranslationsService} from './services/core/translations.service';
import {initializeApp} from './initialize-app';
import {Capacitor} from '@capacitor/core';

Chart.register(...registerables);
registerLocaleData(de, 'de');
Settings.defaultZone = 'Europe/Berlin';
setOptions({
  theme: 'material',
  themeVariant: 'light',
  locale: localeDe
});

Sentry.init({
  dsn: "https://29297dc0f07b9e34cd320ecdba1b880c@o405101.ingest.us.sentry.io/4508935089946624",
  integrations: [
  ],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  beforeSend(event) {
    if (!environment.production) {
      return null;
    }

    return event;
  },
});

const enableSW = !isDevMode() && !Capacitor.isNativePlatform();

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    FormsModule,
    BrowserModule,
    IonicModule.forRoot({
      mode: 'md'
    }),
    AppRoutingModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode() && !Capacitor.isNativePlatform(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000'
    }),
    StdToolbarComponent,
    TranslateModule.forRoot({
      defaultLanguage: 'de'
    }),
  ],
  providers: [
    {
      provide: RouteReuseStrategy,
      useClass: IonicRouteStrategy
    },
    {
      provide: LOCALE_ID,
      useValue: 'de'
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: DeviceInterceptor,
      multi: true
    },
    ServiceFacilitiesService,
    HttpService,
    OrganisationsService,
    SyncService,
    // Tables ----------------------------------------------------------------------------------------------------------
    SyncStatusItemsTable,
    VehiclesTable,
    EmployeesTable,
    ProdRequestsTable,
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [
        TranslationsService,
        StorageService,
        LicensesService,
        OrganisationsService,
        AuthService,
        UserService,
        CustomersService,
        ServiceFacilitiesService,
        DeviceService,
        SyncService,
        ConnectionService,
        SettingsService
      ],
      multi: true
    },
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class AppModule {
}
